/**
 * Hook to submit feedback on a moderation case
 */

import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

import { SUBMIT_CASE_FEEDBACK } from '../graphql';

interface CaseFeedbackInput {
  caseId: string;
  decision: string;
  comments?: string;
  labels?: Array<{
    name: string;
    action: string;
  }>;
}

interface ModerationCase {
  id: string;
  communityId: string;
  topicName: string;
  subjectId: string;
  caseId: string;
  pk: string;
  report: {
    communityId: string;
    topicName: string;
    subjectId: string;
    timestamp: string;
    contents: Array<{
      contentId: string;
      contentType: string;
      data: string;
      labels: Array<{
        labels: Array<{
          label: string;
          confidence: number;
        }>;
        source: string | null;
      }> | null;
    }> | null;
  };
  status: string;
  createdBy: string;
  createdAt: string;
  lastModifiedBy: string;
  lastModifiedAt: string;
}

interface SubmitCaseFeedbackResponse {
  submitCaseFeedback: ModerationCase;
}

/**
 * Hook to submit moderation case feedback
 * @returns Mutation function to submit feedback
 */
export function useSubmitCaseFeedback() {
  const [submitFeedback, { data, loading, error }] = useMutation<SubmitCaseFeedbackResponse>(SUBMIT_CASE_FEEDBACK);

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const submit = async (input: CaseFeedbackInput) =>
    submitFeedback({
      variables: { input },
    });

  return {
    submitFeedback: submit,
    case: data?.submitCaseFeedback,
    loading,
    errors,
  };
}
